import React, { useState, useEffect } from 'react';
import HttpService from '../../service/HttpService';
import Table from './Table';

const LicensesSection = () => {
    const { axiosInstance } = HttpService();
    const [licenses, setLicenses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedLicense, setSelectedLicense] = useState(null);
    const [loadingCreate, setLoadingCreate] = useState(false);
    const [loadingEdit, setLoadingEdit] = useState(false);
    const [errorModal, setErrorModal] = useState({ show: false, message: '' });

    const [newLicense, setNewLicense] = useState({
        name: '',
        maxUsers: 1,
        validUntil: '',
    });

    const fetchLicenses = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/licenses');
            const formattedLicenses = response.data.map((license) => ({
                ...license,
                displayValidUntil: new Intl.DateTimeFormat('en-GB').format(new Date(license.validUntil)), // Format for display
            }));
            setLicenses(formattedLicenses);
        } catch (error) {
            console.error('Failed to fetch licenses:', error);
        } finally {
            setIsLoading(false);
        }
    };
    const handleCreateLicense = async () => {
        if (!newLicense.name || !newLicense.maxUsers || !newLicense.validUntil) {
            setErrorModal({ show: true, message: 'All fields are required.' });
            return;
        }
    
        if (newLicense.name.length > 20) {
            setErrorModal({ show: true, message: 'Name cannot exceed 20 characters.' });
            return;
        }
    
        if (newLicense.maxUsers < 1 || newLicense.maxUsers > 50) {
            setErrorModal({ show: true, message: 'Simultaneous Users must be between 1 and 50.' });
            return;
        }
    
        // Validate the date
        const validDate = !isNaN(Date.parse(newLicense.validUntil));
        if (!validDate) {
            setErrorModal({ show: true, message: 'Invalid date format for Expires In.' });
            return;
        }
    
        setLoadingCreate(true);
        try {
            await axiosInstance.post('/licenses/create', {
                name: newLicense.name,
                maxUsers: newLicense.maxUsers,
                validUntil: `${newLicense.validUntil}T23:59:59.00Z`,
            });
            setShowCreateModal(false);
            fetchLicenses();
        } catch (error) {
            console.error('Failed to create license:', error);
            const errorMessage =
                error.response?.data?.detail || 'An unexpected error occurred.';
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setLoadingCreate(false);
        }
    };

    const handleEditLicense = async () => {
        if (!selectedLicense) return;
    
        if (selectedLicense.maxUsers < 1 || selectedLicense.maxUsers > 50) {
            setErrorModal({ show: true, message: 'Simultaneous Users must be between 1 and 50.' });
            return;
        }
    
        // Validate the date
        const validDate = !isNaN(Date.parse(selectedLicense.validUntil));
        if (!validDate) {
            setErrorModal({ show: true, message: 'Invalid date format for Expires In.' });
            return;
        }
    
        setLoadingEdit(true);
        try {
            await axiosInstance.put(`/licenses/edit/${selectedLicense.licenseHash}`, {
                maxUsers: selectedLicense.maxUsers,
                validUntil: `${selectedLicense.validUntil}T23:59:59.00Z`,
            });
            setShowEditModal(false);
            fetchLicenses();
        } catch (error) {
            console.error('Failed to edit license:', error);
            const errorMessage =
                error.response?.data?.detail || 'An unexpected error occurred.';
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setLoadingEdit(false);
        }
    };

    const columns = [
        { key: 'name', label: 'Name' },
        { key: 'licenseHash', label: 'License Hash' },
        { key: 'maxUsers', label: 'Simultaneous Users' },
        { key: 'displayValidUntil', label: 'Expires In' }, // Use displayValidUntil for formatted date
    ];

    const actions = [
        {
            label: 'Edit',
            className:
                'bg-blue-100 text-blue-600 px-3 py-1 rounded-lg hover:bg-blue-200 transition',
            onClick: (license) => {
                setSelectedLicense({
                    ...license,
                    validUntil: new Date(license.validUntil).toISOString().split('T')[0], // Format to yyyy-MM-dd
                });
                setShowEditModal(true);
            },
        },
    ];

    const openCreateModal = () => {
        setNewLicense({
            name: '',
            maxUsers: 1,
            validUntil: '', // Reset validUntil
        });
        setShowCreateModal(true);
    };

    useEffect(() => {
        fetchLicenses();
    }, []);

    return (
        <section id="licensesSection" className="dashboard-section">
            <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-800">Manage Licenses</h2>
                    <button
                        id="addLicenseBtn"
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center"
                        onClick={openCreateModal}
                    >
                        <i className="fas fa-plus mr-2"></i> Add License
                    </button>
                </div>
                {isLoading ? (
                    <div className="text-center">Loading...</div>
                ) : (
                    <Table columns={columns} data={licenses} actions={actions} />
                )}
            </div>

            {/* Create License Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Create License</h3>
                        <form>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">Name</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={newLicense.name}
                                    onChange={(e) =>
                                        setNewLicense({ ...newLicense, name: e.target.value })
                                    }
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">
                                    Simultaneous Users
                                </label>
                                <input
                                    type="number"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={newLicense.maxUsers}
                                    onChange={(e) =>
                                        setNewLicense({ ...newLicense, maxUsers: parseInt(e.target.value, 10) })
                                    }
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">
                                    Expires In
                                </label>
                                <input
                                    type="date"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={newLicense.validUntil}
                                    onChange={(e) =>
                                        setNewLicense({ ...newLicense, validUntil: e.target.value })
                                    }
                                />
                            </div>
                            <div className="flex justify-between">
                                <button
                                    type="button"
                                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition"
                                    onClick={() => setShowCreateModal(false)}
                                    disabled={loadingCreate}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                                    onClick={handleCreateLicense}
                                    disabled={loadingCreate}
                                >
                                    {loadingCreate ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit License Modal */}
            {showEditModal && selectedLicense && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Edit License</h3>
                        <form>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">
                                    Simultaneous Users
                                </label>
                                <input
                                    type="number"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={selectedLicense.maxUsers}
                                    onChange={(e) =>
                                        setSelectedLicense({
                                            ...selectedLicense,
                                            maxUsers: parseInt(e.target.value, 10),
                                        })
                                    }
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">
                                    Expires In
                                </label>
                                <input
                                    type="date"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={selectedLicense.validUntil}
                                    onChange={(e) =>
                                        setSelectedLicense({
                                            ...selectedLicense,
                                            validUntil: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className="flex justify-between">
                                <button
                                    type="button"
                                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={loadingEdit}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                                    onClick={handleEditLicense}
                                    disabled={loadingEdit}
                                >
                                    {loadingEdit ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Error Modal */}
            {errorModal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md text-center">
                        <h3 className="text-lg font-semibold text-red-600 mb-4">Error</h3>
                        <p className="text-gray-700 mb-4">{errorModal.message}</p>
                        <button
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                            onClick={() => setErrorModal({ show: false, message: '' })}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default LicensesSection;